import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useLandingT } from '../i18n/landingTranslations';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChefHat, Mail, Lock, User, Store, ArrowRight, ArrowLeft, Eye, EyeOff, Coins } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = [
  { code: 'EUR', symbol: '\u20ac', label: 'Euro (EUR)' },
  { code: 'USD', symbol: '$', label: 'Dollar US (USD)' },
  { code: 'THB', symbol: '\u0e3f', label: 'Baht (THB)' },
  { code: 'AED', symbol: 'AED', label: 'Dirham (AED)' },
  { code: 'GBP', symbol: '\u00a3', label: 'Livre Sterling (GBP)' },
  { code: 'CHF', symbol: 'CHF', label: 'Franc Suisse (CHF)' },
  { code: 'MAD', symbol: 'MAD', label: 'Dirham Marocain (MAD)' },
  { code: 'XOF', symbol: 'CFA', label: 'Franc CFA Ouest (XOF)' },
  { code: 'XAF', symbol: 'CFA', label: 'Franc CFA Central (XAF)' },
];

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [mode, setMode] = useState(refCode ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useLandingT(language);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success(t('auth_success_login'));
        navigate('/dashboard');
      } else if (mode === 'register') {
        if (!name || !restaurantName) {
          toast.error(t('auth_fill_all'));
          setLoading(false);
          return;
        }
        await register(email, password, name, restaurantName, currency, refCode || null);
        toast.success(t('auth_success_register'));
        navigate('/dashboard');
      } else if (mode === 'forgot') {
        const res = await fetch(`${API}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (res.ok) {
          toast.success(t('auth_reset_sent'));
          setMode('login');
        } else {
          toast.error(t('auth_error'));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo + Language */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <Link to="/" className="flex items-center gap-2">
            <ChefHat className="w-8 h-8 text-[#D4A853]" />
            <span className="text-2xl font-black text-white tracking-tight">HIBI<span className="text-[#D4A853]">SAAS</span></span>
          </Link>
          <LanguageSelector variant="dark" />
        </div>

        {/* Card */}
        <div className="bg-[#1A1A1A] rounded-3xl border border-[#2A2A2A] p-8" data-testid="auth-card">
          <h1 className="text-xl font-bold text-white mb-1" data-testid="auth-title">
            {mode === 'login' ? t('auth_login_title') : mode === 'register' ? t('auth_register_title') : t('auth_forgot_title')}
          </h1>
          <p className="text-sm text-[#888] mb-6">
            {mode === 'login' ? t('auth_login_desc') : mode === 'register' ? t('auth_register_desc') : t('auth_forgot_desc')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && refCode && (
              <div className="bg-[#0A422D]/20 border border-[#0A422D]/40 rounded-xl p-3 text-center" data-testid="referral-banner">
                <p className="text-xs text-[#D4A853] font-medium">{t('auth_referral_banner')}</p>
              </div>
            )}
            {mode === 'register' && (
              <>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder={t('auth_name_placeholder')} required
                    className="pl-10 h-12 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                    data-testid="register-name" />
                </div>
                <div className="relative">
                  <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)}
                    placeholder={t('auth_restaurant_placeholder')} required
                    className="pl-10 h-12 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                    data-testid="register-restaurant" />
                </div>
                <div className="relative">
                  <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                    className="w-full h-12 pl-10 pr-4 bg-[#111] border border-[#333] text-white rounded-xl text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D4A853]/40"
                    data-testid="register-currency">
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.code} className="bg-[#111]">
                        {c.symbol} — {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth_email_placeholder')} required
                className="pl-10 h-12 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                data-testid="auth-email" />
            </div>

            {mode !== 'forgot' && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth_password_placeholder')} required minLength={6}
                  className="pl-10 pr-10 h-12 bg-[#111] border-[#333] text-white rounded-xl placeholder:text-[#555]"
                  data-testid="auth-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}

            {mode === 'login' && (
              <div className="text-right">
                <button type="button" onClick={() => setMode('forgot')}
                  className="text-xs text-[#888] hover:text-[#D4A853] transition-colors"
                  data-testid="forgot-password-link">
                  {t('auth_forgot_link')}
                </button>
              </div>
            )}

            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold rounded-xl text-sm"
              data-testid="auth-submit">
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>{mode === 'login' ? t('auth_submit_login') : mode === 'register' ? t('auth_submit_register') : t('auth_forgot_submit')} <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>

          {mode === 'forgot' ? (
            <p className="text-center text-sm text-[#888] mt-6">
              <button onClick={() => setMode('login')} className="text-[#D4A853] font-medium hover:underline inline-flex items-center gap-1" data-testid="back-to-login">
                <ArrowLeft className="w-3 h-3" /> {t('auth_back_login')}
              </button>
            </p>
          ) : (
            <>
              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-[#333]" />
                <span className="text-xs text-[#666]">{t('auth_or')}</span>
                <div className="flex-1 h-px bg-[#333]" />
              </div>

              {/* Google button placeholder */}
              <Button variant="outline" className="w-full h-12 bg-transparent border-[#333] text-white rounded-xl hover:bg-[#222] text-sm"
                onClick={() => toast.info(t('auth_google_soon'))}
                data-testid="google-login-btn">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {t('auth_google')}
              </Button>

              {/* Toggle mode */}
              <p className="text-center text-sm text-[#888] mt-6">
                {mode === 'login' ? (
                  <>{t('auth_no_account')} <button onClick={() => setMode('register')} className="text-[#D4A853] font-medium hover:underline" data-testid="switch-to-register">{t('auth_signup_link')}</button></>
                ) : (
                  <>{t('auth_has_account')} <button onClick={() => setMode('login')} className="text-[#D4A853] font-medium hover:underline" data-testid="switch-to-login">{t('auth_login_link')}</button></>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
